import {
  CoreQuery,
  SetF2fDepositPaymentReviewBody,
  SetF2fRemainderPaymentReviewBody,
  SetOnlinePaymentReviewBody,
} from "@gen/app-query";
import { useQueryClient } from "@tanstack/react-query";

interface UsePaymentReviewMutationProps {
  transactionId: number;
  isDepositInReview?: boolean;
  approved?: boolean;
  isRemainderInReview?: boolean;
  isPaymentInReview?: boolean;
}

export function usePaymentReviewMutation({
  transactionId,
  approved,
  isDepositInReview,
  isRemainderInReview,
  isPaymentInReview,
}: UsePaymentReviewMutationProps) {
  const queryClient = useQueryClient();

  const actionText = approved ? "approved" : "rejected";
  let title = "Payment";
  if (isDepositInReview) {
    title = "Deposit payment";
  }
  if (isRemainderInReview) {
    title = "Remainder payment";
  }
  const successMessage = `${title} ${actionText} for transaction: ${transactionId}`;

  const {
    mutate: setF2fDepositPaymentReview,
    isPending: isF2fDepositPaymentReviewSubmitting,
  } = CoreQuery.useAdmin_setF2fDepositPaymentReviewMutationWithParameters({
    meta: {
      success: successMessage,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
      });
    },
  });

  const {
    mutate: setF2fRemainderPaymentReview,
    isPending: isF2fRemainderPaymentReviewSubmitting,
  } = CoreQuery.useAdmin_setF2fRemainderPaymentReviewMutationWithParameters({
    meta: {
      success: successMessage,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
      });
    },
  });

  const {
    mutate: setOnlinePaymentReview,
    isPending: isOnlinePaymentReviewSubmitting,
  } = CoreQuery.useAdmin_setOnlinePaymentReviewMutationWithParameters({
    meta: {
      success: successMessage,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
      });
    },
  });

  const submitPaymentReview = (reason: string) => {
    if (isDepositInReview) {
      setF2fDepositPaymentReview({
        transaction_id: transactionId,
        setF2fDepositPaymentReviewBody: SetF2fDepositPaymentReviewBody.fromJS({
          approved,
          reason: reason.trim(),
        }),
      });
    } else if (isRemainderInReview) {
      setF2fRemainderPaymentReview({
        transaction_id: transactionId,
        setF2fRemainderPaymentReviewBody:
          SetF2fRemainderPaymentReviewBody.fromJS({
            approved,
            reason: reason.trim(),
          }),
      });
    } else if (isPaymentInReview) {
      setOnlinePaymentReview({
        transaction_id: transactionId,
        setOnlinePaymentReviewBody: SetOnlinePaymentReviewBody.fromJS({
          approved,
          reason: reason.trim(),
        }),
      });
    }
  };

  const isLoading =
    (isDepositInReview && isF2fDepositPaymentReviewSubmitting) ||
    (isRemainderInReview && isF2fRemainderPaymentReviewSubmitting) ||
    (isPaymentInReview && isOnlinePaymentReviewSubmitting);

  return {
    submitPaymentReview,
    isLoading,
  };
}
